import { useState, useCallback, useRef } from "react";
import { Plus, Edit, Trash2, FileText, Download, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import { useWorkspaceDocuments } from "../../hooks/useWorkspaceDocuments";
import { WorkspaceDocument } from "../../models/workspaceDocument";
import { getFileInputAcceptString } from "../../lib/fileTypes";
import { validateFileSizesWithToast } from "../../lib/fileUploadValidation";

export function WorkspaceDocumentsPage() {
  const {
    loading,
    documents,
    uploadDocuments,
    updateDocument,
    deleteDocument,
    getDownloadUrl,
    toggleActive,
  } = useWorkspaceDocuments();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] =
    useState<WorkspaceDocument | null>(null);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<WorkspaceDocument | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDocType, setEditDocType] = useState("");
  const [saving, setSaving] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDeleteClick = (document: WorkspaceDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (documentToDelete) {
      await deleteDocument(documentToDelete.id);
      setDocumentToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleUploadClick = () => {
    setSelectedFiles([]);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const selectedFiles = Array.from(files);
    const validFiles = validateFileSizesWithToast(selectedFiles);

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }

    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const success = await uploadDocuments(selectedFiles);
    setUploading(false);

    if (success) {
      setSelectedFiles([]);
      setUploadDialogOpen(false);
    }
  };

  const handleEditClick = (document: WorkspaceDocument) => {
    setDocumentToEdit(document);
    setEditName(document.name ?? "");
    setEditDescription(document.description ?? "");
    setEditDocType(document.docType ?? "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!documentToEdit) return;

    setSaving(true);
    const success = await updateDocument(documentToEdit.id, {
      name: editName || null,
      description: editDescription || null,
      docType: editDocType || null,
    });

    if (success) {
      setEditDialogOpen(false);
      setDocumentToEdit(null);
    }
    setSaving(false);
  };

  const handleDownload = useCallback(
    async (document: WorkspaceDocument) => {
      const downloadUrl = await getDownloadUrl(document.id);
      if (downloadUrl) {
        try {
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = window.document.createElement("a");
          link.href = url;
          link.download = document.name ?? "document";
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error downloading document:", error);
          window.open(downloadUrl, "_blank");
        }
      }
    },
    [getDownloadUrl],
  );

  const handleToggleActive = useCallback(
    async (document: WorkspaceDocument, checked: boolean) => {
      await toggleActive(document.id, checked);
    },
    [toggleActive],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col w-full mx-auto overflow-y-auto">
      <div className="px-4 mx-auto flex flex-col w-full max-w-6xl mt-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Workspace Documents</h1>
            <p className="text-muted-foreground mt-1">
              Manage documents available across all procedures in this workspace
            </p>
          </div>
          <Button onClick={handleUploadClick}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Documents
          </Button>
        </div>

        {/* Documents Table */}
        {documents.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No documents yet
            </h3>
            <p className="text-gray-500 mb-4">
              Upload documents to make them available across all procedures
            </p>
            <Button onClick={handleUploadClick}>
              <Plus className="h-4 w-4 mr-2" />
              Upload Documents
            </Button>
          </div>
        ) : (
          <div className="border-x mb-8">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell className="font-medium">
                      {document.name ?? "Untitled"}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {document.description ?? "-"}
                    </TableCell>
                    <TableCell>
                      {document.docType ? (
                        <Badge variant="outline">{document.docType}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={document.isActive}
                          onCheckedChange={(checked) =>
                            handleToggleActive(document, checked)
                          }
                        />
                        <span
                          className={
                            document.isActive
                              ? "text-green-600"
                              : "text-gray-500"
                          }
                        >
                          {document.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(document.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(document)}
                          title="Edit document"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(document)}
                          title="Download document"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(document)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete document"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select files to upload. Supported formats: PDF, Excel, CSV,
              images, video (MP4/MOV), and ZIP files.
            </p>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-stone-300 rounded-lg p-6">
              <Upload className="h-10 w-10 text-stone-400 mb-3" />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Choose Files
              </Button>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept={getFileInputAcceptString() + ",.zip"}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            {selectedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 text-sm text-stone-600 bg-stone-50 px-3 py-2 rounded"
                  >
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      className="text-stone-400 hover:text-stone-600"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadSubmit}
              disabled={uploading || selectedFiles.length === 0}
              loading={uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Document name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="What this document contains"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="docType">Document Type</Label>
              <Input
                id="docType"
                value={editDocType}
                onChange={(e) => setEditDocType(e.target.value)}
                placeholder="e.g., policy, lookup, guidance"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;
              {documentToDelete?.name ?? "this document"}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
